import { useCallback, useMemo } from 'react';

import type { GridColumnVisibilityModel, GridSortModel } from '@mui/x-data-grid';

import useLocalStorage from './useLocalStorage';

export interface PromoTablePreferences {
  sortModel: GridSortModel;
  columnVisibility: GridColumnVisibilityModel;
  showDateTimes?: boolean;
}

export interface PromoPreferences {
  uiSelection: {
    voucherUsers: PromoTablePreferences;
    voucherLogs: PromoTablePreferences;
  };
}

// Keep promo UI choices together so new tables can extend this without adding scattered keys.
const STORAGE_KEY = 'promo:ui-preferences:v1';

const DEFAULT_PREFERENCES: PromoPreferences = {
  uiSelection: {
    voucherUsers: { sortModel: [], columnVisibility: {}, showDateTimes: false },
    voucherLogs: { sortModel: [], columnVisibility: {} },
  },
};

export function usePromoPreferences(): [
  PromoPreferences,
  (value: PromoPreferences | ((current: PromoPreferences) => PromoPreferences)) => void,
] {
  const [storedPreferences, setStoredPreferences] = useLocalStorage(STORAGE_KEY, DEFAULT_PREFERENCES);

  const preferences = useMemo<PromoPreferences>(
    () => ({
      uiSelection: {
        voucherUsers: {
          ...DEFAULT_PREFERENCES.uiSelection.voucherUsers,
          ...storedPreferences?.uiSelection?.voucherUsers,
        },
        voucherLogs: {
          ...DEFAULT_PREFERENCES.uiSelection.voucherLogs,
          ...storedPreferences?.uiSelection?.voucherLogs,
        },
      },
    }),
    [storedPreferences],
  );

  const updatePreferences = useCallback(
    (value: PromoPreferences | ((current: PromoPreferences) => PromoPreferences)) => setStoredPreferences(value),
    [setStoredPreferences],
  );

  return [preferences, updatePreferences];
}
